import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';
import { SupabaseService } from '../services/supabaseService';

const AuthContext = createContext<any>({
    session: null,
    user: null,
    loading: true,
    authReady: false,
    signOut: async () => { },
    signInGuest: async (token?: string) => { }
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [session, setSession] = useState<any | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [authReady, setAuthReady] = useState(false);

    const ensureProfile = async (currentUser: any) => {
        if (currentUser) {
            try {
                // Ensure profile exists in DB
                await SupabaseService.getProfile();
            } catch (error) {
                console.error("AuthContext: Failed to ensure profile", error);
            }
        }
    };

    useEffect(() => {
        let mounted = true;

        // Initial session fetch
        const initializeAuth = async () => {
            try {
                const { data, error } = await supabase.auth.getSession();
                if (error) {
                    console.error("Failed to get initial session", error);
                }

                if (!mounted) return;

                if (data?.session) {
                    setSession(data.session);
                    setUser(data.session.user);
                }
            } catch (e) {
                console.error("Failed to initialize auth session", e);
            } finally {
                if (mounted) {
                    setLoading(false);
                    setAuthReady(true);
                }
            }
        };

        initializeAuth();

        // Listen for changes (initial load, sign in, sign out, refresh, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event: any, session: any) => {
            if (!mounted) return;

            // Update state with the latest session from the event
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
            setAuthReady(true);
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    // Post-auth effect to ensure profile and handle migrations
    useEffect(() => {
        let mounted = true;

        const setupUserProfile = async () => {
            if (!authReady || !user?.id) return;

            // We have a user, ensure profile and check migrations
            await ensureProfile(user);

            // LEGACY MIGRATION LOGIC
            const guestUserId = localStorage.getItem('guest_user_id');
            if (guestUserId) {
                console.log("Found guest data to migrate:", guestUserId);
                try {
                    const { error } = await supabase.rpc('migrate_guest_data', {
                        guest_id: guestUserId,
                        new_owner_id: user.id
                    });

                    if (!error) {
                        console.log("Guest data migrated successfully.");
                        localStorage.removeItem('guest_user_id');
                        localStorage.removeItem('guest_current_war');
                    } else {
                        console.error("Migration failed:", error);
                    }
                } catch (err) {
                    console.error("Migration exception:", err);
                }
            }
        };

        setupUserProfile();

        return () => {
            mounted = false;
        };
    }, [authReady, user?.id]);

    const signOut = async () => {
        await supabase.auth.signOut();
    };

    const signInGuest = async (token?: string) => {
        const { error } = await supabase.auth.signInAnonymously({
            options: {
                captchaToken: token
            }
        });
        if (error) throw error;
    };

    return (
        <AuthContext.Provider value={{
            session,
            user,
            loading,
            authReady,
            signOut,
            signInGuest
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
